import GameModel from "../../GameModel";
import Projection from "../../Projection";
import { Settings } from "../../Settings";
import Fast from "../../util/Fast";
import Crew from "../crew/Crew";
import PerformanceMetrics from "../performance/PerformanceMetrics";
import Grid from "./Grid";
import GridLoader from "./GridLoader";
import WorldGrid from "./WorldGrid";

/**
 * @constant
 * @type {number}
 */
const NORTH = 0;
/**
 * @constant
 * @type {number}
 */
const NORTH_WEST = 1;
/**
 * @constant
 * @type {number}
 */
const NORTH_EAST = 2;
/**
 * @constant
 * @type {number}
 */
const WEST = 3;
/**
 * @constant
 * @type {number}
 */
const EAST = 4;
/**
 * @constant
 * @type {number}
 */
const SOUTH = 5;
/**
 * @constant
 * @type {number}
 */
const SOUTH_WEST = 6;
/**
 * @constant
 * @type {number}
 */
const SOUTH_EAST = 7;


export default class GridLoadingManager extends GameModel {
    /**
     * @param {WorldGrid} worldGrid 
     * @param {Projection} projection 
     */
    constructor(worldGrid, projection) {
        super();

        /**
         * @constant
         * @type {WorldGrid}
         */
        this.worldGrid = worldGrid;
        /**
         * @constant
         * @type {Projection}
         */
        this.projection = projection;

        /**
         * @type {Grid}
         */
        this.centerGrid = null;

        /**
         * @type {number}
         */
        this.loadedGridTurn = 0;

        /**
         * See initializeLoadingManager()
         * @type {GridLoader}
         */
        this.gridLoader = null;
        /**
         * See initializeLoadingManager()
         * @type {Crew}
         */
        this.crew = null;

        /**
         * @constant
         * @type {Grid[]}
         */
        this.savedGrids = new Array(9);

    }

    /**
     * Invoked by State when the game is loading.
     * @param {GridLoader} gridLoader
     * @param {Crew} crew
     */
    initializeLoadingManager(gridLoader, crew) {
        this.gridLoader = gridLoader;
        this.crew = crew;


        const gridPixelWidth = Settings.grid.gridPixelWidth;
        const gridPixelHeight = Settings.grid.gridPixelHeight;

        const grids = this.worldGrid.grids;

        // Set the origin of the center grid to match projection.
        const gridCol = Fast.floor(this.projection.gridCenter.x / gridPixelWidth);
        const gridRow = Fast.floor(this.projection.gridCenter.y / gridPixelHeight);

        const cx = gridCol * gridPixelWidth;
        const cy = gridRow * gridPixelHeight;

        // Top Row
        this.gridLoader.loadGrid(grids[0], cx - gridPixelWidth, cy - gridPixelHeight); // NORTH WEST
        this.gridLoader.loadGrid(grids[1], cx, cy - gridPixelHeight); // NORTH
        this.gridLoader.loadGrid(grids[2], cx + gridPixelWidth, cy - gridPixelHeight); // NORTH EAST

        // Middle Row
        this.gridLoader.loadGrid(grids[3], cx - gridPixelWidth, cy);  // WEST
        this.gridLoader.loadGrid(grids[4], cx, cy);
        this.gridLoader.loadGrid(grids[5], cx + gridPixelWidth, cy); // EAST

        // Bottom Row
        this.gridLoader.loadGrid(grids[6], cx - gridPixelWidth, cy + gridPixelHeight); // SOUTH WEST
        this.gridLoader.loadGrid(grids[7], cx, cy + gridPixelHeight); // SOUTH
        this.gridLoader.loadGrid(grids[8], cx + gridPixelWidth, cy + gridPixelHeight); // SOUTH EAST

        this.performPostGridLoadOperations();

        // Increment the world grid change count.
        this.worldGrid.changeCount++;
        this.worldGrid.shiftCount++;

    }

    /**
     * @private
     * @param {Grid} grid 
     */
    clearSavedGrid(grid) {
        const i = this.savedGrids.indexOf(grid);
        if (i !== -1) {
            this.savedGrids[i] = null;
        }
        else {
            console.log('GridLoadingManager.clearSavedGrid() did not find grid in saved grids');
        }
    }

    /**
     * @private
     * @param {Grid} centerGrid 
     * @param {number} gridIndex 
     * @param {number} direction 
     */
    setGridIfAlreadyLoaded(centerGrid, gridIndex, direction) {
        
        const savedGridIndex = this.findSavedGridIndex(centerGrid, direction);

        if (savedGridIndex === -1) {
            return;
        }

        // If we successfully find the grid in the saved grids, then we know that it is loaded.

        const grid = this.savedGrids[savedGridIndex];

        // The grid only counts if it is loaded.
        if (!grid.loaded) {
            return;
        }

        this.savedGrids[savedGridIndex] = null;

        this.worldGrid.grids[gridIndex] = grid;

    }

    /**
     * @private
     * @returns {number}
     */
    findFirstSavedGridIndex() {
        // Find the first non-null grid in saved grids.
        for (let i = 0; i < this.savedGrids.length; i++) {
            if (this.savedGrids[i]) {
                return i;
            }
        }

        return -1;
    }

    /**
     * @private
     * @param {Grid} centerGrid 
     * @param {number} gridIndex 
     * @param {number} direction 
     */
    loadIfUnloaded(centerGrid, gridIndex, direction) {

        // If the grid is in the correct spot and loaded, then we are done.
        const currentGrid = this.worldGrid.grids[gridIndex];
        if (currentGrid && currentGrid.isLoaded()) {
            return;
        }

        const recycledGridIndex = this.findFirstSavedGridIndex();
        if (recycledGridIndex === -1) {
            console.log('GridLoadingManager.loadIfUnloaded() ERROR Failed to find available grid!');
            return;
        }
        
        const grid = this.savedGrids[recycledGridIndex];

        this.worldGrid.grids[gridIndex] = grid;
        this.savedGrids[recycledGridIndex] = null;

        // Assign the new origin to the grid
        const cx = centerGrid.origin.x;
        const cy = centerGrid.origin.y;

        let x = cx;
        let y = cy;

        const gridWidth = Settings.grid.gridPixelWidth;
        const gridHeight = Settings.grid.gridPixelHeight;

        switch (direction) {
            case NORTH_WEST:
                x = cx - gridWidth;
                y = cy - gridHeight;
                break;
            case NORTH:
                y = cy - gridHeight;
                break;
            case NORTH_EAST:
                x = cx + gridWidth;
                y = cy - gridHeight;
                break;
            case WEST:
                x = cx - gridWidth;
                break;
            case EAST:
                x = cx + gridWidth;
                break;
            case SOUTH_WEST:
                y = cy + gridHeight;
                x = cx - gridWidth;
                break;
            case SOUTH:
                y = cy + gridHeight;
                break;
            case SOUTH_EAST:
                y = cy + gridHeight;
                x = cx + gridWidth;
                break;
            default:
                console.log("GridLoadingManager.findSavedGridIndex() Unknown direction: " + direction);
                return -1;
        }

        // Load up the grid.        
        this.gridLoader.loadGrid(grid, x, y);
    }

    /**
     * @private
     * @param {Grid} centerGrid 
     * @param {number} direction 
     * @returns {number}
     */
    findSavedGridIndex(centerGrid, direction) {
        const tileCol = centerGrid.getOriginTileCol();
        const tileRow = centerGrid.getOriginTileRow();

        const tileWidth = Settings.grid.gridTileWidth;
        const tileHeight = Settings.grid.gridTileHeight;

        let targetTileCol = tileCol;
        let targetTileRow = tileRow;

        // The origin is the bottom left corner of the box.
        // The y-coordinate increases as we go downwards.

        switch (direction) {
            case NORTH_WEST:
                targetTileRow = tileRow - tileHeight;
                targetTileCol = tileCol - tileWidth;
                break;
            case NORTH:
                targetTileRow = tileRow - tileHeight;
                break;
            case NORTH_EAST:
                targetTileRow = tileRow - tileHeight;
                targetTileCol = tileCol + tileWidth;
                break;
            case WEST:
                targetTileCol = tileCol - tileWidth;
                break;
            case EAST:
                targetTileCol = tileCol + tileWidth;
                break;
            case SOUTH_WEST:
                targetTileRow = tileRow + tileHeight;
                targetTileCol = tileCol - tileWidth;
                break;
            case SOUTH:
                targetTileRow = tileRow + tileHeight;
                break;
            case SOUTH_EAST:
                targetTileRow = tileRow + tileHeight;
                targetTileCol = tileCol + tileWidth;
                break;
            default:
                console.log("GridLoadingManager.findSavedGridIndex() Unknown direction: " + direction);
                return -1;
        }

        for (let i = 0; i < this.savedGrids.length; i++) {
            const current = this.savedGrids[i];
            if (!current) {
                continue;
            }

            const currentTileCol = current.getOriginTileCol();
            if (currentTileCol !== targetTileCol) {
                continue;
            }

            const currentTileRow = current.getOriginTileRow();
            if (currentTileRow === targetTileRow) {
                return i;
            }
        }

        return -1;
    }

    /**
     */
    manageLoadedGridsForFrame() {

        const gridCenter = this.projection.gridCenter;

        // Try to find the center grid based on the currently loaded grids.
        let newCenterGrid = this.worldGrid.findGridContaining(gridCenter);

        // If the center grid is not loaded, we are going to have to ...
        if (!newCenterGrid) {
            // Just reassign the grid at [4] as the new center grid.
            newCenterGrid = this.worldGrid.grids[4];
            if (newCenterGrid.isLoaded()) {
                this.gridLoader.unloadGrid(newCenterGrid); // Save grid state, return stuff to cache.
            }

            // Set the origin of the grid before we load it

            // 1. convert pixels to grid col/rows
            const gridCol = Fast.floor(gridCenter.x / Settings.grid.gridPixelWidth); 
            const gridRow = Fast.floor(gridCenter.y / Settings.grid.gridPixelHeight);

            // 2. convert grid col/rows back into pixels.
            const x = gridCol * Settings.grid.gridPixelWidth;
            const y = gridRow * Settings.grid.gridPixelHeight;
            this.gridLoader.loadGrid(newCenterGrid, x, y);

            // We need to set the current centerGrid to null to force our newCenterGrid to become current.
            this.centerGrid = null;
        }

        // If the center grid has not changed, then there is nothing to do.
        if (this.centerGrid && this.centerGrid === newCenterGrid) {
            return;
        }

        // Increment the world grid change count.
        this.worldGrid.changeCount++;
        this.worldGrid.shiftCount++;

        // Update the center grid.
        this.centerGrid = newCenterGrid;

        // Copy current grids to backupGrids
        const grids = this.worldGrid.grids;
        for (let i = 0; i < grids.length; i++) {
            this.savedGrids[i] = grids[i];
            grids[i] = null;
        }

        // Set center grid to [4]
        grids[4] = this.centerGrid;
        this.clearSavedGrid(this.centerGrid);

        // Sanity check: I don't think its possible for the center grid to not be loaded at this point.
        // if (!this.centerGrid.isLoaded()) {
        //     console.log('GridLoadingManager.manageLoadedGridsForFrame() ERROR - center grid not loaded!');
        //     return;
        // }

        ///////////////////////////////////////////////////
        // Load adjacent grids.
        ///////////////////////////////////////////////////

        this.setGridIfAlreadyLoaded(this.centerGrid, 0, NORTH_WEST);
        this.setGridIfAlreadyLoaded(this.centerGrid, 1, NORTH);
        this.setGridIfAlreadyLoaded(this.centerGrid, 2, NORTH_EAST);

        this.setGridIfAlreadyLoaded(this.centerGrid, 3, WEST);
        // center is already done
        this.setGridIfAlreadyLoaded(this.centerGrid, 5, EAST);

        this.setGridIfAlreadyLoaded(this.centerGrid, 6, SOUTH_WEST);
        this.setGridIfAlreadyLoaded(this.centerGrid, 7, SOUTH);
        this.setGridIfAlreadyLoaded(this.centerGrid, 8, SOUTH_EAST);

        ///////////////////////////////////////////////////
        // Handle new grids
        ///////////////////////////////////////////////////

        this.loadIfUnloaded(this.centerGrid, 0, NORTH_WEST);
        this.loadIfUnloaded(this.centerGrid, 1, NORTH);
        this.loadIfUnloaded(this.centerGrid, 2, NORTH_EAST);

        this.loadIfUnloaded(this.centerGrid, 3, WEST);
        // center is already done
        this.loadIfUnloaded(this.centerGrid, 5, EAST);

        this.loadIfUnloaded(this.centerGrid, 6, SOUTH_WEST);
        this.loadIfUnloaded(this.centerGrid, 7, SOUTH);
        this.loadIfUnloaded(this.centerGrid, 8, SOUTH_EAST);

        ///////////////////////////////////////////////////
        // Post grid load operations
        ///////////////////////////////////////////////////


        PerformanceMetrics.startTime('PostGridLoading');
        this.performPostGridLoadOperations();
        PerformanceMetrics.endTime('PostGridLoading');


        // this.loadCount++;
        // if (this.loadCount == 20) {
        //     PerformanceMetrics.printMetrics();
        //     this.loadCount = 0;
        // }
    }

    /**
     * @private
     */
    performPostGridLoadOperations() {

        // Set the grid neighbors
        this.setAllGridNeighbors();        

        this.findAllGridBorderTileNeighbors();

        this.evaulateGridTiles();

        this.onLoadingComplete();
        
        this.validateTileReferences();


        // Clear out distant grids (but only if the crew has been initialized)
        if (this.crew && this.crew.main) {
            let crewGrid = null;

            const crewTile = this.crew.main.position.tile;
            if (crewTile) {
                crewGrid = crewTile.getGrid();
            }
            else if (this.centerGrid) {
                crewGrid = this.centerGrid.getWorldGrid().findGridContaining(this.crew.main.position.worldCoordinateOrigin);
            }

            if (crewGrid) {
                this.gridLoader.gridSaveManager.clearDistantGrids(this.centerGrid, crewGrid);
            }
        }
    }

    /**
     * @private
     */
    setAllGridNeighbors() {
        const grids = this.worldGrid.grids;

        // [0][1][2]
        // [3][4][5]
        // [6][7][8]

        // setGridNeighbors(top, bottom, right, left)

        // Top row 
        grids[0].setGridNeighbors(null, grids[3], grids[1], null);      // top left
        grids[1].setGridNeighbors(null, grids[4], grids[2], grids[0]);  // top center
        grids[2].setGridNeighbors(null, grids[5], null, grids[1]);      // top right

        // Center row
        grids[3].setGridNeighbors(grids[0], grids[6], grids[4], null);      // center left
        grids[4].setGridNeighbors(grids[1], grids[7], grids[5], grids[3]);  // center
        grids[5].setGridNeighbors(grids[2], grids[8], null, grids[4]);      // center right

        // Bottom row
        grids[6].setGridNeighbors(grids[3], null, grids[7], null);      // center left
        grids[7].setGridNeighbors(grids[4], null, grids[8], grids[6]);  // center
        grids[8].setGridNeighbors(grids[5], null, null, grids[7]);      // center right
    }

    /**
     * @private
     */
    onLoadingComplete() {
        const grids = this.worldGrid.grids;
        for (let i = 0; i < grids.length; i++) {
            if (!grids[i].loaded) {
                this.gridLoader.onTerrainGenerationComplete(grids[i]);
            }
        }
    }

    /**
     * @private
     */
    findAllGridBorderTileNeighbors() {
        const grids = this.worldGrid.grids;
        for (let i = 0; i < grids.length; i++) {
            // We need to find neighbors for all grids, not just the loaded ones.
            // If we don't, then loaded grids that have shifted position in the world grid
            // may now have a border with the edge of the world and we'll need to nullify some tile neighbors.

            grids[i].findWorldGridTileNeighbors();
        }
    }

    /**
     * @private
     */
    evaulateGridTiles() {
        const grids = this.worldGrid.grids;
        for (let i = 0; i < grids.length; i++) {
            const grid = grids[i];
            if (!grid.loaded) {
                grid.evaluateAllTileBorders();
            }
            else {
                // Loaded grids probably just got a new neighbor, so we should re-evaluate the grid borders.
                grid.evaluateWorldGridTileNeighborBorders();
            }
        }
    }

    /**
     * @private
     */
    validateTileReferences() {
        const grids = this.worldGrid.grids;
        for (let i = 0; i < grids.length; i++) {
            this.validateGridTileReferences(grids[i]);
        }
    }

    /**
     * @private
     * @param {Grid} grid;
     */
    validateGridTileReferences(grid) {
        const characters = grid.characters;

        for (let c = 0; c < characters.length; c++) {
            const position = characters[c].position;
            const valid = position.tile && position.tile.contains(position.worldCoordinateOrigin);

            // should we iterate over path plan and validate the tiles somehow?
            // should we check that the target tile is valid?
            // find other tile references.

            if (!valid) {
                console.log('GridLoadingManager.validateGridTileReferences() found invalid character. ' + position.worldCoordinateOrigin.toString());
                characters[c].onInvalidGrid();
            }
        }
    }

    regenerateGrids() {
        const grids = this.worldGrid.grids;
        for (let i = 0; i < grids.length; i++) {
            const grid = grids[i];
            if (grid) {
                this.gridLoader.regenerateGrid(grid);
            }
        }

        // Increment the world grid change count.
        this.worldGrid.changeCount++;
    }


    // resetGameModels() {
    //     this.crew = null;
    //     this.gridLoader = null;
    // }

    ////////////////////////////////////
    // GameModel
    ////////////////////////////////////

    /**
     * @override
     */
    resetFull() {
        // Unload the grids.
        const grids = this.worldGrid.grids;
        for (let i = 0; i < grids.length; i++) {
            const grid = grids[i];
            if (grid) {
                this.gridLoader.unloadGrid(grid);
            }
        }

        // The crew and grid loader are set by state.intializeState()
        this.crew = null;
        this.gridLoader = null;
    }

    /**
     * @override
     */
    resetForPrestige() {
        this.resetFull();
    }

}
